import { forwardRef, useEffect, RefObject, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { Direction } from '../types';
import { Conversation } from '@/components/Conversation';
import useConversationMessage from '@/hooks/useConversationMessage';
import { ChatMetadataParams, useChatMetadata } from '@/hooks/useChatMetadata';
import useCookies from '@/hooks/useCookies';
import { Button } from '@/components/shadcn/button';
import { RefreshCcw } from 'lucide-react';

const WebConversation = forwardRef(
  (
    {
      newMessageScrollTarget,
      className,
    }: {
      className?: string;
      newMessageScrollTarget?: RefObject<HTMLDivElement>;
    },
    ref,
  ) => {
    const { sessionID, setSessionID } = useCookies();
    const { id } = useParams();

    const chatParams: ChatMetadataParams = {
      chatId: id!,
      sessionID,
    };

    const { data: chatMetadata, refetch } = useChatMetadata(chatParams);
    const { loading, sendMessage, messages, initChat } = useConversationMessage(chatParams);

    useEffect(() => {
      if (!chatMetadata) return;

      // Set document direction based on chatbot's configured direction (not language)
      document.documentElement.setAttribute(
        'dir',
        chatMetadata.direction === Direction.RTL ? 'rtl' : 'ltr',
      );

      document.title = chatMetadata.name;

      initChat(chatMetadata);

      // Cleanup: reset to LTR when component unmounts
      return () => {
        document.documentElement.setAttribute('dir', 'ltr');
      };
    }, [chatMetadata, id]);

    const restartConversation = useCallback(() => {
      setSessionID(uuidv4());
    }, [setSessionID]);

    useEffect(() => {
      refetch();
    }, [sessionID, id]);

    if (!chatMetadata) {
      return null;
    }

    return (
      <>
        {chatMetadata.isRefreshPossible && (
          <Button
            onClick={restartConversation}
            className="fixed top-0 mt-6 w-fit px-3! z-100"
            variant={'outline'}
          >
            <RefreshCcw size={16} />
          </Button>
        )}
        <Conversation
          newMessageScrollTarget={newMessageScrollTarget}
          className={className}
          chatMetadata={chatMetadata}
          messages={messages}
          loading={loading}
          onSend={sendMessage}
        />
      </>
    );
  },
);

WebConversation.displayName = 'WebConversation';

export { WebConversation };
